// SCR-STAFF-888 (AP-ACC-02) — گزارش چک‌ها (US-ACC-052؛ AC-ACC-052.1): چک‌های دریافتنی BIL و پرداختنی INV و CMP با وضعیت و سررسید و پیش‌بینی
// نقدینگی ۳۰ و ۹۰ روز آینده از چک‌های باز؛ چک‌های تسویه CMP بدون پیگیری سررسید و وصول‌اند (C-AGENT-1449).
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type AccChequeReportItem,
  type AccChequeReportStatus,
  type AccMoney,
} from '@phoenix/api-client';
import { useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  DataTable,
  JalaliDate,
  JalaliDatePickerRHF,
  ListPage,
  StatusChip,
  toBusinessDate,
  type DataTableColumn,
  type StatusTone,
} from '@phoenix/ui';
import { useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { accKeys, Amount, SCR_STAFF_888, useBranchName } from './common';

interface RangeForm {
  from: string | null;
  to: string | null;
}

/** بازه پیش‌فرض: ۳۰ روز گذشته تا ۹۰ روز آینده (منطق خالص؛ تست Vitest). */
export function defaultChequeRange(today: Date): { from: string; to: string } {
  const shift = (days: number) => {
    const date = new Date(today);
    date.setDate(date.getDate() + days);
    return toBusinessDate(date);
  };
  return { from: shift(-30), to: shift(90) };
}

const TONES: Record<AccChequeReportStatus, StatusTone> = {
  Open: 'pending',
  Cleared: 'done',
  Bounced: 'no_show',
  Returned: 'inactive',
  Untracked: 'inactive',
};

function Figure({ labelKey, money, testId }: { labelKey: string; money: AccMoney; testId: string }) {
  const { t } = useTranslation('acc');
  return (
    <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 160 }}>
      <Typography variant="body2" color="text.secondary">
        {t(labelKey)}
      </Typography>
      <Typography variant="h6" component="p">
        <Amount money={money} testId={testId} />
      </Typography>
    </Paper>
  );
}

export function ChequesPage() {
  const { t } = useTranslation('acc');
  const { tenantApi, session } = useTenantSession();
  const branchName = useBranchName();
  const { control, watch } = useForm<RangeForm>({ defaultValues: defaultChequeRange(new Date()) });
  const from = watch('from');
  const to = watch('to');
  const report = useQuery({
    queryKey: accKeys.cheques(session?.tenantId, `${from}|${to}`),
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/accounting/reports/cheques', {
          params: { query: { from: from ?? undefined, to: to ?? undefined } },
        }),
      ),
  });
  const forecast = report.data?.forecast;
  const columns: DataTableColumn<AccChequeReportItem>[] = [
    {
      id: 'due',
      headerKey: 'acc:cheque.due',
      cell: (row) => <JalaliDateCell value={row.dueDate} overdue={row.isOverdue} />,
    },
    {
      id: 'direction',
      headerKey: 'acc:cheque.direction',
      cell: (row) => t(`acc:cheque_direction.${row.direction}`),
    },
    { id: 'source', headerKey: 'acc:cheque.source', cell: (row) => t(`acc:cheque_source.${row.source}`) },
    { id: 'branch', headerKey: 'acc:common.branch', cell: (row) => branchName(row.branchId) },
    { id: 'amount', headerKey: 'acc:common.amount', cell: (row) => <Amount money={row.amount} /> },
    {
      id: 'status',
      headerKey: 'acc:common.status',
      cell: (row) => (
        <span data-testid="acc-cheque-status" data-status={row.status} data-source={row.source}>
          <StatusChip tone={TONES[row.status]} labelKey={`acc:cheque_status.${row.status}`} />
        </span>
      ),
    },
  ];
  return (
    <ListPage
      screenId={SCR_STAFF_888}
      titleKey="acc:cheque.title"
      subtitleKey="acc:cheque.subtitle"
      filters={
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <JalaliDatePickerRHF control={control} name="from" labelKey="acc:cheque.due_from" />
          <JalaliDatePickerRHF control={control} name="to" labelKey="acc:cheque.due_to" />
        </Stack>
      }
    >
      {forecast ? (
        <Stack spacing={1} sx={{ mb: 2 }} component="section" aria-label={t('acc:cheque.forecast')}>
          <Typography variant="subtitle1">{t('acc:cheque.forecast')}</Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <Figure
              labelKey="acc:cheque.receivable_overdue"
              money={forecast.receivableOverdue}
              testId="acc-forecast-receivable-overdue"
            />
            <Figure
              labelKey="acc:cheque.receivable_30"
              money={forecast.receivable30}
              testId="acc-forecast-receivable-30"
            />
            <Figure
              labelKey="acc:cheque.receivable_90"
              money={forecast.receivable90}
              testId="acc-forecast-receivable-90"
            />
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <Figure
              labelKey="acc:cheque.payable_overdue"
              money={forecast.payableOverdue}
              testId="acc-forecast-payable-overdue"
            />
            <Figure
              labelKey="acc:cheque.payable_30"
              money={forecast.payable30}
              testId="acc-forecast-payable-30"
            />
            <Figure
              labelKey="acc:cheque.payable_90"
              money={forecast.payable90}
              testId="acc-forecast-payable-90"
            />
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <Figure labelKey="acc:cheque.net_30" money={forecast.net30} testId="acc-forecast-net-30" />
            <Figure labelKey="acc:cheque.net_90" money={forecast.net90} testId="acc-forecast-net-90" />
          </Stack>
        </Stack>
      ) : null}
      <DataTable
        captionKey="acc:cheque.title"
        columns={columns}
        rows={report.data?.items}
        getRowId={(row) => `${row.source}-${row.id}`}
        isLoading={report.isPending}
        error={report.error}
        onRetry={() => void report.refetch()}
        emptyTitleKey="acc:cheque.empty"
      />
    </ListPage>
  );
}

function JalaliDateCell({ value, overdue }: { value: string; overdue: boolean }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <JalaliDate value={value} />
      {overdue ? <StatusChip tone="no_show" labelKey="acc:cheque.overdue" /> : null}
    </Stack>
  );
}
